import { MultiPhaseAutomatedReleaseDescription } from '@gonfalon/automated-rollouts/components/MultiPhaseAutomatedReleaseDescription';
import { getFlagVariationIndexFromId } from '@gonfalon/flag-variations/getFlagVariationIndexFromId';
import { VariationDisplay } from '@gonfalon/flag-variations/VariationDisplay';
import nullthrows from 'nullthrows';

import { RolloutInstructionDescription } from '#components/automated-rollouts/guarded-rollouts/review-save';
import type { Flag } from '#utils/flags/records/Flag';
import type { StartAutomatedReleaseSemanticInstruction } from '#utils/instructions/automated-release/types';

import { ClauseInstructionDescription } from './ClauseInstructionDescription';

export function StartAutomatedReleaseInstructionEntry({
  flag,
  projKey,
  environmentKey,
  instruction,
}: {
  flag: Flag;
  projKey: string;
  environmentKey: string;
  instruction: StartAutomatedReleaseSemanticInstruction;
}) {
  const {
    ruleId,
    originalVariationId,
    targetVariationId,
    randomizationUnit,
    stages,
    audiences,
    clauses,
    metrics: metricSources,
    metricMonitoringPreferences,
    updateDefaultRuleOnCompletion,
  } = instruction;

  const monitoringPreferences = metricMonitoringPreferences ?? {};
  const variations = flag.variations.toArray();
  const renderVariation = (variationId: string) => {
    const index = getFlagVariationIndexFromId(variations, variationId);
    return index === -1 ? (
      <code>VARIATION NOT FOUND</code>
    ) : (
      <VariationDisplay variation={variations[index]} variationIndex={index} />
    );
  };

  // Multi-phase releases (two or more audiences) render as phase cards plus a shared section,
  // matching the targeting review modal. Single-phase releases keep the inline rollout description.
  if (audiences && audiences.length >= 2) {
    const metrics = (metricSources ?? [])
      .filter((source) => !source.isGroup)
      .map((source) => ({ key: source.key, autoRollback: monitoringPreferences[source.key]?.autoRollback ?? false }));

    return (
      <MultiPhaseAutomatedReleaseDescription
        phases={audiences.map((audience) => ({
          description: audience.description,
          clauses: audience.clauses.map((clause, idx) => (
            <ClauseInstructionDescription
              key={idx}
              projKey={projKey}
              envKey={environmentKey}
              clause={clause}
              extraText={idx !== audience.clauses.length - 1 ? ' and ' : undefined}
            />
          )),
          stages: audience.stages.map((stage) => ({
            rolloutWeight: stage.allocation,
            monitoringWindowMilliseconds: stage.durationMillis,
          })),
        }))}
        originalVariationId={originalVariationId}
        targetVariationId={targetVariationId}
        rolloutContextKind={randomizationUnit}
        metrics={metrics}
        updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
        renderVariation={renderVariation}
      />
    );
  }

  const metrics = (metricSources ?? [])
    .filter((source) => !source.isGroup)
    .map((source) => ({
      metricKey: source.key,
      onRegression: { rollback: monitoringPreferences[source.key]?.autoRollback ?? false },
    }));

  const phase = audiences?.[0] ?? { description: undefined, clauses: clauses ?? [], stages: stages ?? [] };
  const isNewRule = phase.clauses.length > 0;

  let ruleName: string;
  if (isNewRule) {
    ruleName = 'rule';
  } else if (ruleId === undefined) {
    ruleName = 'default rule';
  } else {
    const ruleIndex = flag.getRuleIndex(environmentKey, ruleId);
    if (ruleIndex > -1) {
      const rule = nullthrows(
        flag.getRules(environmentKey)?.get(ruleIndex),
        `expected flag to have rule in environment with key "${environmentKey}" at index ${ruleIndex}`,
      );
      ruleName = rule.description || `Rule ${ruleIndex + 1}`;
    } else {
      ruleName = 'Rule not found';
    }
  }

  return (
    <>
      <span>Start release on {ruleName} </span>
      {phase.description ? <span>{phase.description}: </span> : null}
      {phase.clauses.map((clause, idx) => (
        <ClauseInstructionDescription
          key={idx}
          projKey={projKey}
          envKey={environmentKey}
          clause={clause}
          extraText={idx !== phase.clauses.length - 1 ? ' and ' : undefined}
        />
      ))}
      <RolloutInstructionDescription
        originalVariationId={originalVariationId}
        targetVariationId={targetVariationId}
        rolloutContextKind={randomizationUnit}
        variations={variations}
        metrics={metrics}
        stages={[
          ...phase.stages.map((stage) => ({
            rolloutWeight: stage.allocation,
            monitoringWindowMilliseconds: stage.durationMillis,
          })),
          { rolloutWeight: 100000 },
        ]}
        updateDefaultRuleOnCompletion={updateDefaultRuleOnCompletion}
      />
    </>
  );
}
